<template lang="pug">
div
  PDFModal(
    ref="pdfModal",
    :class_options="[]",
    :config="config",
    @saved="performSearch",
    :detail_list="currentDetailList"
  )

  base-header(ref="base")
    b-row.py-3(align-v="center")
      b-col(cols="12")
        h6.h2.text-white.d-inline-block.mb-0
          i.fa.fa-search.text-muted.mr-2
          | Globale Suche
        .small.text-muted(v-if="totalRows > 0") {{ totalRows }} Dokumente gefunden

  b-container(fluid).mt-4
    // Search Form
    b-card.mb-4
      b-row
        b-col(cols="12")
          b-form(@submit.prevent="performSearch")
            b-input-group
              b-form-input(
                v-model="searchText",
                placeholder="Suche in allen Dokumenten (* als Platzhalter)...",
                size="lg",
                @keyup.enter="performSearch"
              )
              b-input-group-append
                b-button(
                  variant="primary",
                  @click="performSearch",
                  :disabled="loading"
                )
                  i.fa.fa-search.mr-1(v-if="!loading")
                  b-spinner(small, v-if="loading")
                  |  Suchen
            b-form-group.search-in-pages-checkbox.mt-2.mb-0
              b-form-checkbox(v-model="searchInPages", @change="onSearchInPagesChange")
                | In Volltext suchen (PDF-/OCR-Text)
            b-row.mt-2(v-if="searchInPages")
              b-col(cols="12", md="6", lg="4")
                b-form-group(label="Projekt")
                  el-select.w-100(
                    v-model="selectedProjectIds",
                    placeholder="Alle Projekte",
                    :filterable="true",
                    multiple,
                    clearable,
                    size="small"
                  )
                    el-option(
                      v-for="project in projects",
                      :key="project._id",
                      :label="project.name",
                      :value="project._id"
                    )

      // Advanced Filters Toggle
      b-row.mt-3(v-if="!searchInPages")
        b-col(cols="12")
          b-link(@click="showAdvancedFilters = !showAdvancedFilters")
            i.fa.mr-1(:class="showAdvancedFilters ? 'fa-chevron-up' : 'fa-chevron-down'")
            | Erweiterte Filter {{ showAdvancedFilters ? 'ausblenden' : 'anzeigen' }}

      // Advanced Filters
      b-collapse(:visible="showAdvancedFilters", v-if="!searchInPages").mt-3
        b-row
          b-col(cols="12", md="6", lg="3")
            b-form-group(label="Dateiname")
              b-form-input(
                v-model="filters.filename",
                placeholder="Dateiname oder Pfad...",
                size="sm"
              )

          b-col(cols="12", md="6", lg="3")
            b-form-group(label="KKS")
              b-form-input(
                v-model="filters.selected_kks",
                placeholder="KKS suchen...",
                size="sm"
              )

          b-col(cols="12", md="6", lg="3")
            b-form-group(label="DCC-Klasse")
              b-form-input(
                v-model="filters.selected_class",
                placeholder="DCC-Klasse...",
                size="sm"
              )

        b-row
          b-col(cols="12", md="6", lg="3")
            b-form-group(label="Geprüft")
              b-select(
                v-model="filters.reviewed",
                :options="reviewedOptions",
                size="sm"
              )

          b-col(cols="12", md="6", lg="3")
            b-form-group(label="Projekt")
              el-select.w-100(
                v-model="selectedProjectIds",
                placeholder="Alle Projekte",
                :filterable="true",
                multiple,
                clearable,
                size="small"
              )
                el-option(
                  v-for="project in projects",
                  :key="project._id",
                  :label="project.name",
                  :value="project._id"
                )

        // Extracted Data Filters (directly editable key/value rows)
        b-row.mt-2
          b-col(cols="12")
            h6.text-muted Sachmerkmale Filter
        b-row(v-for="(row, rowIndex) in extractedDataFilterRows", :key="rowIndex").align-items-end.mb-2
          b-col(cols="12", md="4")
            b-form-group(label="Schlüssel", :label-for="'sm-key-' + rowIndex")
              el-select.sachmerkmale-key-select(
                v-model="row.key",
                size="small",
                filterable,
                allow-create,
                default-first-option,
                placeholder="Schlüssel wählen oder eingeben",
                :style="{ width: '100%' }"
              )
                el-option(value="", label="— Kein Filter —")
                el-option(value="__all__", label="Alle Schlüssel (beliebiger Key)")
                el-option(v-for="optKey in sachmerkmaleKeyOptions", :key="optKey", :label="optKey", :value="optKey")
          b-col(cols="12", md="4")
            b-form-group(label="Wert", :label-for="'sm-value-' + rowIndex")
              b-form-input(
                v-model="row.value",
                placeholder="z.B. Siemens",
                size="sm"
              )
          b-col(cols="12", md="4").d-flex.align-items-end
            b-button(
              v-if="extractedDataFilterRows.length > 1",
              variant="outline-danger",
              size="sm",
              @click="removeExtractedDataFilterRow(rowIndex)",
              v-b-tooltip.hover,
              title="Zeile entfernen"
            ).mb-2
              i.fa.fa-trash-alt
            b-button(
              v-if="rowIndex === extractedDataFilterRows.length - 1",
              variant="outline-secondary",
              size="sm",
              @click="addExtractedDataFilterRow",
              v-b-tooltip.hover,
              title="Weitere Zeile"
            ).mb-2.ml-1
              i.fa.fa-plus

        b-row.mt-3
          b-col(cols="12")
            b-button(
              variant="outline-secondary",
              size="sm",
              @click="resetFilters"
            )
              i.fa.fa-undo.mr-1
              | Filter zurücksetzen

    // Results Table
    b-card
      template(v-if="loading")
        .text-center.py-5
          b-spinner(variant="primary")
          .mt-2 Suche läuft...

      template(v-else-if="documents.length === 0 && hasSearched")
        .text-center.py-5.text-muted
          i.fa.fa-search.fa-3x.mb-3
          h5 Keine Dokumente gefunden
          p Versuchen Sie andere Suchbegriffe oder Filter.

      template(v-else-if="documents.length === 0 && !hasSearched")
        .text-center.py-5.text-muted
          i.fa.fa-search.fa-3x.mb-3
          h5 Starten Sie eine Suche
          p Geben Sie einen Suchbegriff ein oder verwenden Sie die erweiterten Filter.

      template(v-else)
        // Results Header
        .d-flex.justify-content-between.align-items-center.mb-3
          span {{ totalRows }} Dokumente gefunden
          b-form-group.mb-0
            b-input-group(size="sm")
              b-input-group-prepend
                b-input-group-text Sortieren nach
              b-select(
                v-model="sortBy",
                :options="sortOptions",
                size="sm",
                @change="performSearch"
              )
              b-select(
                v-model="orderBy",
                :options="orderOptions",
                size="sm",
                @change="performSearch"
              )

        // Results Table
        b-table(
          :items="documents",
          :fields="tableFields",
          responsive,
          hover,
          small,
          striped
        )
          template(#cell(filename)="data")
            .d-flex.flex-column
              b-link.text-decoration-none(
                @click="openPdfModal(data.item)",
                href="javascript:void(0)"
              )
                i.fa.fa-file-pdf.mr-1.text-danger
                | {{ data.item.filename }}
              .small.text-muted.text-truncate(style="max-width: 300px") {{ data.item.filepath }}

          template(#cell(project_name)="data")
            b-link(
              :to="'/dashboard/' + data.item.project_id",
              v-b-tooltip.hover,
              :title="'Zum Projekt: ' + data.item.project_name"
            )
              i.fa.fa-folder.mr-1
              | {{ data.item.project_name }}

          template(#cell(selected_kks)="data")
            template(v-if="data.item.selected_kks && data.item.selected_kks.length > 0")
              span(v-for="(kks, idx) in data.item.selected_kks.slice(0, 3)", :key="idx")
                b-badge.mr-1(variant="secondary", pill) {{ kks }}
              span(v-if="data.item.selected_kks.length > 3") +{{ data.item.selected_kks.length - 3 }}
            template(v-else)
              span.text-muted -

          template(#cell(selected_class)="data")
            template(v-if="data.item.selected_class && data.item.selected_class.length > 0")
              span(v-for="(cls, idx) in data.item.selected_class.slice(0, 2)", :key="idx")
                b-badge.mr-1(variant="info", pill) {{ cls }}
              span(v-if="data.item.selected_class.length > 2") +{{ data.item.selected_class.length - 2 }}
            template(v-else)
              span.text-muted -

          template(#cell(best_date)="data")
            span(v-if="data.item.best_date") {{ data.item.best_date }}
            span.text-muted(v-else) -

          template(#cell(reviewed)="data")
            i.fa.fa-check-circle.text-success(v-if="data.item.reviewed")
            i.fa.fa-circle.text-muted(v-else)

          template(#cell(extracted_information_count)="data")
            b-badge(v-if="data.item.extracted_information_count > 0", variant="warning", pill)
              | {{ data.item.extracted_information_count }} SM

          template(#cell(actions)="data")
            b-button-group(size="sm")
              b-button(
                variant="outline-primary",
                @click="openPdfModal(data.item)",
                v-b-tooltip.hover,
                title="PDF öffnen"
              )
                i.fa.fa-eye
              b-button(
                variant="outline-secondary",
                :to="'/dashboard/' + data.item.project_id + '?openDocument=' + data.item._id",
                v-b-tooltip.hover,
                title="Im Projekt öffnen"
              )
                i.fa.fa-external-link-alt

        // Pagination
        b-row.mt-3
          b-col.d-flex.justify-content-between.align-items-center
            span.small.text-muted
              | Seite {{ currentPage }} von {{ Math.ceil(totalRows / pageSize) }}
              | ({{ pageSize }} pro Seite)
            b-pagination(
              v-model="currentPage",
              :total-rows="totalRows",
              :per-page="pageSize",
              @change="onPageChange",
              align="right"
            )
</template>

<script>
import PDFModal from './PDFModal.vue';

export default {
  name: 'GlobalSearch',
  components: {
    PDFModal
  },
  data() {
    return {
      searchText: '',
      searchInPages: false,
      showAdvancedFilters: false,
      loading: false,
      hasSearched: false,
      documents: [],
      totalRows: 0,
      currentPage: 1,
      pageSize: 50,
      sortBy: 'created_at',
      orderBy: 'desc',
      projects: [],
      projectsInfo: {},
      selectedProjectIds: [],
      config: {},
      currentDetailList: { entries: [], all_classes: [] },
      checklists: [],

      // Field filters
      filters: {
        filename: '',
        selected_kks: '',
        selected_class: '',
        reviewed: null
      },

      // Extracted data filters (editable key/value rows)
      extractedDataFilterRows: [{ key: '', value: '' }],

      // Options
      reviewedOptions: [
        { value: null, text: 'Alle' },
        { value: true, text: 'Geprüft' },
        { value: false, text: 'Nicht geprüft' }
      ],
      sortOptions: [
        { value: 'created_at', text: 'Erstelldatum' },
        { value: 'filename', text: 'Dateiname' },
        { value: 'project_id', text: 'Projekt' }
      ],
      orderOptions: [
        { value: 'desc', text: 'Absteigend' },
        { value: 'asc', text: 'Aufsteigend' }
      ],

      // Table fields
      tableFields: [
        { key: 'filename', label: 'Dokument', sortable: false },
        { key: 'project_name', label: 'Projekt', sortable: false },
        { key: 'selected_kks', label: 'KKS', sortable: false },
        { key: 'selected_class', label: 'DCC', sortable: false },
        { key: 'best_date', label: 'Datum', sortable: false },
        { key: 'reviewed', label: 'Geprüft', sortable: false, class: 'text-center' },
        { key: 'extracted_information_count', label: 'SM', sortable: false, class: 'text-center' },
        { key: 'actions', label: 'Aktionen', sortable: false, class: 'text-center' }
      ]
    };
  },

  computed: {
    sachmerkmaleKeyOptions() {
      const keys = new Set();
      (this.checklists || []).forEach((checklist) => {
        (checklist.checklist_entries || []).forEach((entry) => {
          if (entry.item_to_be_checked) {
            keys.add(entry.item_to_be_checked);
          }
        });
      });
      return Array.from(keys).sort();
    }
  },

  mounted() {
    this.loadProjects();
    this.loadConfig();
    this.loadChecklists();
  },

  methods: {
    async loadProjects() {
      try {
        const response = await this.axios.get(this.base_url + '/projects');
        this.projects = this.$_.sortBy(response.data, 'name');
      } catch (error) {
        console.error('Error loading projects:', error);
      }
    },

    async loadConfig() {
      try {
        const response = await this.axios.get(this.base_url + '/config');
        this.config = response.data;
      } catch (error) {
        console.error('Error loading config:', error);
        this.config = {};
      }
    },

    async loadChecklists() {
      try {
        const response = await this.axios.get(this.base_url + '/docsense/checklists');
        this.checklists = response.data || [];
      } catch (error) {
        console.error('Error loading checklists:', error);
        this.checklists = [];
      }
    },

    async performSearch() {
      const trimmedSearchText = (this.searchText || '').trim();
      const usePageTextSearch = this.searchInPages;
      if (usePageTextSearch && trimmedSearchText.length > 0 && trimmedSearchText.length < 3) {
        this.$bvToast.toast('Volltextsuche in Seiten benötigt mindestens 3 Zeichen.', {
          title: 'Hinweis',
          variant: 'warning',
          solid: true
        });
        return;
      }

      this.loading = true;
      this.hasSearched = true;

      try {
        // Build request payload
        const payload = {
          text_search: this.searchText || null,
          search_in_pages: usePageTextSearch,
          filter: usePageTextSearch ? null : this.buildFieldFilters(),
          extracted_data_filter: usePageTextSearch ? null : this.buildExtractedDataFilter(),
          project_ids: this.selectedProjectIds.length > 0 
            ? this.selectedProjectIds 
            : null,
          page_number: this.currentPage,
          page_size: this.pageSize,
          sort_by: this.sortBy,
          order_by: this.orderBy
        };

        const response = await this.axios.post(
          this.base_url + '/global_search',
          payload
        );

        this.documents = response.data.rows;
        this.totalRows = response.data.total_rows;
        this.projectsInfo = response.data.projects || {};

      } catch (error) {
        console.error('Search error:', error);
        this.$bvToast.toast('Fehler bei der Suche: ' + ((error.response && error.response.data && error.response.data.detail) || error.message), {
          title: 'Fehler',
          variant: 'danger',
          solid: true
        });
        this.documents = [];
        this.totalRows = 0;
      } finally {
        this.loading = false;
      }
    },

    onSearchInPagesChange(value) {
      if (value) {
        this.showAdvancedFilters = false;
      }
    },

    buildFieldFilters() {
      const filters = {};
      
      if (this.filters.filename) {
        filters.filename = this.filters.filename;
      }
      if (this.filters.selected_kks) {
        filters.selected_kks = this.filters.selected_kks;
      }
      if (this.filters.selected_class) {
        filters.selected_class = this.filters.selected_class;
      }
      if (this.filters.reviewed !== null) {
        filters.reviewed = this.filters.reviewed;
      }

      return Object.keys(filters).length > 0 ? filters : null;
    },

    buildExtractedDataFilter() {
      const filter = {};
      this.extractedDataFilterRows.forEach((row) => {
        if (row.key && row.key.trim() !== '') {
          filter[row.key.trim()] = (row.value && row.value.trim()) ? row.value.trim() : '';
        }
      });
      return Object.keys(filter).length > 0 ? filter : null;
    },

    addExtractedDataFilterRow() {
      this.extractedDataFilterRows.push({ key: '', value: '' });
    },

    removeExtractedDataFilterRow(index) {
      this.extractedDataFilterRows.splice(index, 1);
      if (this.extractedDataFilterRows.length === 0) {
        this.extractedDataFilterRows = [{ key: '', value: '' }];
      }
    },

    resetFilters() {
      this.searchText = '';
      this.searchInPages = false;
      this.filters = {
        filename: '',
        selected_kks: '',
        selected_class: '',
        reviewed: null
      };
      this.extractedDataFilterRows = [{ key: '', value: '' }];
      this.selectedProjectIds = [];
      this.currentPage = 1;
    },

    onPageChange(page) {
      this.currentPage = page;
      this.performSearch();
    },

    async openPdfModal(document) {
      // Load detail list for the project if we don't have it
      try {
        const response = await this.axios.get(
          this.base_url + '/projects/' + document.project_id + '/detail_list'
        );
        this.currentDetailList = response.data || { entries: [], all_classes: [] };
      } catch (error) {
        console.error('Error loading detail list:', error);
        this.currentDetailList = { entries: [], all_classes: [] };
      }

      // Open PDF Modal
      this.$refs.pdfModal.open({
        project_id: document.project_id,
        _id: document._id
      });
    },

  }
};
</script>

<style scoped>
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-in-pages-checkbox {
  border: none !important;
}

.search-in-pages-checkbox ::v-deep .custom-control-label::before {
  border: 2px solid #6c757d !important;
  box-shadow: none !important;
}
</style>
